import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box } from '@mui/material';

export default function ResizableButton({ 
  element, 
  updateElement, 
  isInZone = false, 
  layoutId = null, 
  zoneId = null,
  children,
  onButtonClick,
  onDoubleClick,
  isEditing = false,
  disableResize = false
}) {
  // Resize functionality
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [resizeStartData, setResizeStartData] = useState(null);
  const [currentWidth, setCurrentWidth] = useState(() => {
    if (element?.styles?.width) {
      if (typeof element.styles.width === 'number') return element.styles.width;
      if (typeof element.styles.width === 'string') {
        // Handle percentage widths
        if (element.styles.width.includes('%')) {
          return element.styles.width;
        }
        const numValue = parseInt(element.styles.width, 10);
        return isNaN(numValue) ? 200 : numValue;
      }
    }
    return 200;
  });
  const [currentHeight, setCurrentHeight] = useState(() => {
    if (element?.styles?.height) {
      if (typeof element.styles.height === 'number') return element.styles.height;
      if (typeof element.styles.height === 'string') {
        const numValue = parseInt(element.styles.height, 10);
        return isNaN(numValue) ? 48 : numValue;
      }
    }
    return 48;
  });
  
  const containerRef = useRef(null);

  // Update local dimensions when element changes (but not during resize)
  useEffect(() => {
    if (element?.styles?.width && !isResizing) {
      let widthValue;
      if (typeof element.styles.width === 'number') {
        widthValue = element.styles.width;
      } else if (element.styles.width.includes('%')) {
        widthValue = element.styles.width;
      } else {
        widthValue = parseInt(element.styles.width, 10) || 200;
      }
      setCurrentWidth(widthValue);
    }
    if (element?.styles?.height && !isResizing) {
      const heightValue = typeof element.styles.height === 'number' 
        ? element.styles.height 
        : parseInt(element.styles.height, 10) || 48;
      setCurrentHeight(heightValue);
    }
  }, [element?.styles?.width, element?.styles?.height, isResizing]);

  // Resize functionality
  const handleResizeStart = useCallback((direction, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    setResizeDirection(direction);
    setResizeStartData({
      mouseX: e.clientX,
      mouseY: e.clientY,
      width: currentWidth,
      height: currentHeight
    });
  }, [currentWidth, currentHeight]);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !resizeStartData) return;
    
    e.preventDefault();
    
    const deltaX = e.clientX - resizeStartData.mouseX;
    const deltaY = e.clientY - resizeStartData.mouseY;
    
    let newWidth = resizeStartData.width;
    let newHeight = resizeStartData.height;
    
    // Calculate size changes based on resize direction
    if (resizeDirection === 'se') {
      // Bottom-right corner - increase both width and height
      newWidth = Math.max(100, resizeStartData.width + deltaX);
      newHeight = Math.max(40, resizeStartData.height + deltaY);
    } else if (resizeDirection === 'sw') {
      // Bottom-left corner - decrease width, increase height
      newWidth = Math.max(100, resizeStartData.width - deltaX);
      newHeight = Math.max(40, resizeStartData.height + deltaY);
    } else if (resizeDirection === 'ne') {
      // Top-right corner - increase width, decrease height
      newWidth = Math.max(100, resizeStartData.width + deltaX);
      newHeight = Math.max(40, resizeStartData.height - deltaY);
    } else if (resizeDirection === 'nw') {
      // Top-left corner - decrease both width and height
      newWidth = Math.max(100, resizeStartData.width - deltaX);
      newHeight = Math.max(40, resizeStartData.height - deltaY);
    } else if (resizeDirection === 'e') {
      // Right edge - only change width
      newWidth = Math.max(100, resizeStartData.width + deltaX);
    } else if (resizeDirection === 'w') {
      // Left edge - only change width
      newWidth = Math.max(100, resizeStartData.width - deltaX);
    } else if (resizeDirection === 's') {
      // Bottom edge - only change height
      newHeight = Math.max(40, resizeStartData.height + deltaY);
    } else if (resizeDirection === 'n') {
      // Top edge - only change height
      newHeight = Math.max(40, resizeStartData.height - deltaY);
    }
    
    // Apply max size limits
    newWidth = Math.min(800, newWidth);
    newHeight = Math.min(200, newHeight);
    
    // Update local state for immediate visual feedback
    setCurrentWidth(newWidth);
    setCurrentHeight(newHeight);
  }, [isResizing, resizeStartData, resizeDirection]);

  const handleResizeEnd = useCallback(() => {
    const finalWidth = currentWidth;
    const finalHeight = currentHeight;
    
    setIsResizing(false);
    setResizeDirection(null);
    setResizeStartData(null);
    
    // Update element with final dimensions
    updateElement(element.id, {
      styles: {
        ...element.styles,
        width: finalWidth,
        height: finalHeight
      }
    }, isInZone, layoutId, zoneId);
  }, [currentWidth, currentHeight, element.id, element.styles, updateElement, isInZone, layoutId, zoneId]);

  // Global mouse events for resizing
  useEffect(() => {
    if (isResizing) {
      const handleGlobalResizeMove = (e) => handleResizeMove(e);
      const handleGlobalResizeEnd = (e) => handleResizeEnd();
      
      document.addEventListener('mousemove', handleGlobalResizeMove);
      document.addEventListener('mouseup', handleGlobalResizeEnd);
      
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'nw-resize';
      
      return () => {
        document.removeEventListener('mousemove', handleGlobalResizeMove);
        document.removeEventListener('mouseup', handleGlobalResizeEnd);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const renderResizeHandle = (direction, style, cursor) => (
    <Box
      sx={{
        position: 'absolute',
        width: 8,
        height: 8,
        backgroundColor: '#2630f3',
        border: '2px solid #ffffff',
        borderRadius: '50%',
        cursor: cursor || 'nw-resize',
        opacity: 0.8,
        '&:hover': {
          opacity: 1,
          transform: 'scale(1.2)'
        },
        transition: 'all 0.2s',
        zIndex: 10,
        ...style
      }}
      onMouseDown={(e) => handleResizeStart(direction, e)}
    />
  );

  return (
    <Box
      sx={{
        position: 'relative',
        display: typeof currentWidth === 'string' && currentWidth.includes('%') ? 'block' : 'inline-block',
        '&:hover .resize-handles': {
          opacity: 1
        }
      }}
      ref={containerRef}
    >
      {/* The button content */}
      <Box
        sx={{
          width: currentWidth,
          height: currentHeight,
          position: 'relative'
        }}
      >
        {children}
      </Box>

      {/* Resize handles - only show when not editing and resize is not disabled */}
      {!isEditing && !disableResize && (
        <Box className="resize-handles" sx={{ opacity: 0, transition: 'opacity 0.2s' }}>
          {/* Corner handles */}
          {renderResizeHandle('nw', { top: -4, left: -4 }, 'nw-resize')}
          {renderResizeHandle('ne', { top: -4, right: -4 }, 'ne-resize')}
          {renderResizeHandle('sw', { bottom: -4, left: -4 }, 'sw-resize')}
          {renderResizeHandle('se', { bottom: -4, right: -4 }, 'se-resize')}
          
          {/* Edge handles */}
          {renderResizeHandle('n', { top: -4, left: '50%', transform: 'translateX(-50%)' }, 'ns-resize')}
          {renderResizeHandle('s', { bottom: -4, left: '50%', transform: 'translateX(-50%)' }, 'ns-resize')}
          {renderResizeHandle('e', { top: '50%', right: -4, transform: 'translateY(-50%)' }, 'ew-resize')}
          {renderResizeHandle('w', { top: '50%', left: -4, transform: 'translateY(-50%)' }, 'ew-resize')}
        </Box>
      )}
    </Box>
  );
}
